import React from "react";

import starryNight from "@common/assets/images/releases/starry_night.png";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { vars } from "@common/styles";
import { ReleaseMetricCard, ReleaseMetricCardBaseProps } from "@main/pages/Release/views/MetricCards/ReleaseMetricCard";
import styled, { keyframes } from "styled-components";

type ReleaseReadinessCardProps = ReleaseMetricCardBaseProps & {
  targetScore?: number;
};

const GaugeWrap = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ReleaseHealthCard = ({ style, score, targetScore, highlight, error, onMoreClick }: ReleaseReadinessCardProps) => {
  return (
    <ReleaseMetricCard
      style={style}
      title="Release health"
      highlight={highlight}
      error={error}
      onMoreClick={onMoreClick}
    >
      <GaugeWrap>
        <ReadinessGauge
          score={score ?? 0}
          targetScore={targetScore}
        />
      </GaugeWrap>
    </ReleaseMetricCard>
  );
};

export { ReleaseHealthCard };

interface ReadinessGaugeProps {
  score: number;
  targetScore?: number;
}

const CX = 120;
const CY = 120;
const R = 90;
const STROKE_W = 22;
const PLACEHOLDER_COLOR = vars.colors.grey80;
const SHOW_TARGET_INDICATOR = false;
const BG_W = 130;
const BG_H = 70;

const GRADIENT_STOPS: { pos: number; rgb: [number, number, number] }[] = [
  { pos: 0.0, rgb: [176, 2, 101] },
  { pos: 0.1106, rgb: [227, 31, 33] },
  { pos: 0.2933, rgb: [210, 114, 33] },
  { pos: 0.5, rgb: [223, 205, 9] },
  { pos: 0.7019, rgb: [137, 200, 19] },
  { pos: 0.851, rgb: [108, 179, 8] },
  { pos: 1.0, rgb: [0, 127, 19] },
];

// The arc is drawn as many short, solid-colour segments that follow the circle.
// A high count keeps the gradient smooth without visible banding. SVG has no
// conic gradient, and a masked CSS conic gradient (foreignObject) does not paint
// once the SVG is scaled by its viewBox — so segments are the robust approach.
const SEGMENTS_FULL_CIRCLE = 240;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function pointOnCircle(fractionFromTop: number): { x: number; y: number } {
  const angleDeg = -90 + fractionFromTop * 360;
  return {
    x: CX + R * Math.cos(toRad(angleDeg)),
    y: CY + R * Math.sin(toRad(angleDeg)),
  };
}

// Interpolates the gradient color at a given fraction of the full circle.
function colorAt(fraction: number): string {
  const f = Math.max(0, Math.min(1, fraction));
  let lo = GRADIENT_STOPS[0];
  let hi = GRADIENT_STOPS[GRADIENT_STOPS.length - 1];
  for (let i = 0; i < GRADIENT_STOPS.length - 1; i++) {
    if (f >= GRADIENT_STOPS[i].pos && f <= GRADIENT_STOPS[i + 1].pos) {
      lo = GRADIENT_STOPS[i];
      hi = GRADIENT_STOPS[i + 1];
      break;
    }
  }
  const span = hi.pos - lo.pos || 1;
  const t = (f - lo.pos) / span;
  const r = Math.round(lo.rgb[0] + t * (hi.rgb[0] - lo.rgb[0]));
  const g = Math.round(lo.rgb[1] + t * (hi.rgb[1] - lo.rgb[1]));
  const b = Math.round(lo.rgb[2] + t * (hi.rgb[2] - lo.rgb[2]));
  return `rgb(${r}, ${g}, ${b})`;
}

function arcSegment(f0: number, f1: number): string {
  const start = pointOnCircle(f0);
  const end = pointOnCircle(f1);
  return `M ${start.x} ${start.y} A ${R} ${R} 0 0 1 ${end.x} ${end.y}`;
}

const fadeInArc = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ArcGroup = styled.g`
  animation: ${fadeInArc} 0.9s ease-out;
`;

const ReadinessGauge = ({ score, targetScore }: ReadinessGaugeProps) => {
  const clampedScore = Math.max(0, Math.min(10, score));
  const clampedTarget = targetScore != null ? Math.max(0, Math.min(10, targetScore)) : null;

  const scoreFraction = clampedScore / 10;
  const indicator = pointOnCircle(scoreFraction);
  const indicatorColor = colorAt(scoreFraction);
  const targetIndicator = clampedTarget != null ? pointOnCircle(clampedTarget / 10) : null;

  const arcStart = pointOnCircle(0);
  const segmentCount = Math.max(2, Math.round(SEGMENTS_FULL_CIRCLE * scoreFraction));
  const scoreLabel = clampedScore.toFixed(1) === "10.0" ? "10" : clampedScore.toFixed(1);

  return (
    <svg
      viewBox="0 0 240 240"
      preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height: "100%", maxWidth: 320, maxHeight: 320, display: "block" }}
    >
      {/* Decorative starry backdrop, centred in the circle and scaling with it */}
      <image
        href={starryNight}
        x={CX - BG_W / 2}
        y={CY - BG_H / 2}
        width={BG_W}
        height={BG_H}
        preserveAspectRatio="xMidYMid meet"
        style={{ pointerEvents: "none" }}
      />

      {/* Gray placeholder full circle — always visible beneath the arc */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke={PLACEHOLDER_COLOR}
        strokeWidth={STROKE_W}
      />

      {/* Gradient arc: many short solid-colour segments (butt caps) that follow
          the circle, so the colour sweeps smoothly (magenta → red → orange →
          yellow → green) with no visible banding or scalloping. */}
      {scoreFraction > 0 && (
        <>
          <ArcGroup>
            {Array.from({ length: segmentCount }, (_, j) => {
              const f0 = scoreFraction * (j / segmentCount);
              // Overlap each segment slightly into the next to avoid hairline seams.
              const f1 = scoreFraction * ((j + (j < segmentCount - 1 ? 1.5 : 1)) / segmentCount);
              const mid = scoreFraction * ((j + 0.5) / segmentCount);
              return (
                <path
                  key={j}
                  d={arcSegment(f0, f1)}
                  fill="none"
                  stroke={colorAt(mid)}
                  strokeWidth={STROKE_W}
                  strokeLinecap="butt"
                />
              );
            })}
          </ArcGroup>
          {/* Rounded start cap at 12 o'clock, in the gradient's start colour */}
          <circle
            cx={arcStart.x}
            cy={arcStart.y}
            r={STROKE_W / 2}
            fill={colorAt(0)}
          />
        </>
      )}

      {/* Target indicator dot — kept in code, hidden for now via SHOW_TARGET_INDICATOR */}
      {SHOW_TARGET_INDICATOR && targetIndicator != null && (clampedTarget ?? 0) > 0 && (
        <>
          <circle
            cx={targetIndicator.x}
            cy={targetIndicator.y}
            r={8}
            fill="#1C1C1E"
            stroke="#FFFFFF"
            strokeWidth={2.5}
            style={{ cursor: "pointer" }}
          />
          <foreignObject
            x={targetIndicator.x - 10}
            y={targetIndicator.y - 10}
            width={20}
            height={20}
            style={{ overflow: "visible", cursor: "pointer" }}
          >
            <Tooltip
              text={`Target is ${clampedTarget?.toFixed(1)}`}
              position="top"
            >
              <div style={{ width: 20, height: 20, borderRadius: "50%", cursor: "pointer" }} />
            </Tooltip>
          </foreignObject>
        </>
      )}

      {/* Score indicator dot — filled with the gradient color beneath it */}
      <circle
        cx={indicator.x}
        cy={indicator.y}
        r={15}
        fill={indicatorColor}
        stroke="#FFFFFF"
        strokeWidth={3}
        // style={{ cursor: "pointer" }}
      />
      <foreignObject
        x={indicator.x - 10}
        y={indicator.y - 10}
        width={20}
        height={20}
        style={{ overflow: "visible" /*cursor: "pointer"*/ }}
      >
        <div style={{ width: 20, height: 20, borderRadius: "50%" }} />
      </foreignObject>

      {/* Center score */}
      <text
        x={CX}
        y={CY + 16}
        textAnchor="middle"
        fill={vars.colors.white}
        fontSize={48}
        fontWeight={700}
        fontFamily="Roobert, serif"
      >
        {scoreLabel}
      </text>
    </svg>
  );
};
